import React, { useEffect, useMemo, useRef, useState } from 'react'
import CustomButton from '../../features/customButton/ui/main/CustomButton'
import { wheelContent } from '../../../constants/copyright'
import { safeHTML } from '../../../utils/safeHTML'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { useContent } from '../../../redux/reducer/content'
import classNames from 'classnames'
import Picture from '../../baseComponents/gui/picture/Picture'
import { getRandomFromRange } from '../../../utils/data/random/getRandomFromRange'
import useStateControls from '../../../hooks/useStateControls'
import { settings, wheelAnimationLogic } from './wheelAnimationLogic'
import { useModal } from '../../../hooks/useModal'
import { getReward } from '../../../utils/helpers'
import { useTranslation } from '../../../translations'
import { fromTimeStampFull } from '../../../utils/helpers/helpers'
import { useLanguageControls } from '../../../hooks/useLanguageControls'
import { cloneDeep, isNumber, shuffle } from 'lodash'
import { getRandomFromArray } from '../../../utils/scene/utils/random/randomFromRange'
import {
  SHOP_ITEM_IMAGE_SWAP_DELAY_MS,
  SHOP_ITEM_KEYS,
  SHOP_ITEM_UI_SHOW_DELAY_MS,
} from '../../../constants/wheel'

const { title, titlePrize, arrow, button, promocodeModal } = wheelContent
const getShopItemData = (prize = {}) => {
  const hasShopItemReward = SHOP_ITEM_KEYS.some((key) => Boolean(prize?.[key]))
  const previewImage = SHOP_ITEM_KEYS.map((key) => prize?.[key]?.preview_image).find(
    (previewImage) => typeof previewImage === 'string' && previewImage.trim(),
  )

  return { hasShopItemReward, previewImage }
}
const getFirstFilledString = (...values) =>
  values.find((value) => typeof value === 'string' && value.trim()) ?? null

const Wheel = React.forwardRef(function ({ callbacks }, ref) {
  const {
    lootBoxes,
    activeLootBoxPrize: { id, type, prizeData },
    pendingGachaReward,
  } = useContent()

  const listRef = useRef()
  const prizesRef = useRef([])
  const prizeValueRef = useRef()
  const currentAnimation = useRef()
  const backgroundEls = useRef([])
  const backgroundContainerRef = useRef()
  const arrowRef = useRef()
  const counterParticlesRef = useRef([])
  const particlesContainerRef = useRef()
  const { t } = useTranslation()
  const [isRollEnd, setIsRollEnd] = useState(false)
  const [isShowBackBtn, setIsShowBackBtn] = useState(false)
  const [isShopItemImageVisible, setIsShopItemImageVisible] = useState(false)
  const [isShopItemUiVisible, setIsShopItemUiVisible] = useState(false)
  const [elsOnBackgroundData, setElsOnBackgroundData] = useState({})
  const [prizeValue, setPrizeValue] = useState(0)
  const [onBackClick, setOnBackClick] = useState(false)
  const { addModal } = useModal()
  const { isArabic } = useLanguageControls()

  const { setState } = useStateControls()

  const filteredPrizes = useMemo(() => {
    const fullPrizesArray = lootBoxes[type]?.prizes ?? []
    return fullPrizesArray?.reduce(
      (acc, prize) => {
        if (prize?.is_visible && prize?.id !== id) acc.push(prize)
        return acc
      },
      [fullPrizesArray.find(({ id: prizeId }) => prizeId === id) ?? {}],
    )
  }, [lootBoxes, type, id])

  const rotateCount = useMemo(() => {
    if (!filteredPrizes?.length) return
    return Math.ceil(settings.minEls / filteredPrizes?.length)
  }, [filteredPrizes])

  const prizes = useMemo(() => {
    const repeatCount = Number(isNumber(rotateCount) && rotateCount) + 1
    const totalArray = []
    for (let i = 0; i < repeatCount; i++) totalArray.push(...shuffle(cloneDeep(filteredPrizes)))
    return totalArray
  }, [rotateCount, filteredPrizes])

  const necessaryPrizeIndex = useMemo(
    () =>
      prizes.reduce((acc, { id: prizeId }, index) => {
        return typeof acc === 'object'
          ? acc
          : prizeId === id && ++acc === rotateCount
          ? { index }
          : acc
      }, 0)?.index,
    [prizes],
  )

  const currentPrize = prizes[necessaryPrizeIndex]
  const { hasShopItemReward: isCurrentShopItemPrize, previewImage: shopItemPreviewImage } = useMemo(
    () => getShopItemData(prizeData),
    [prizeData],
  )
  const currentShopItemPreviewImage = useMemo(
    () => shopItemPreviewImage ?? prizeData?.image,
    [shopItemPreviewImage, prizeData],
  )
  const rewardTitle =
    isCurrentShopItemPrize && getFirstFilledString(prizeData?.gacha_loot_title, prizeData?.title)
      ? getFirstFilledString(prizeData?.gacha_loot_title, prizeData?.title)
      : t(currentPrize?.title)
  const rewardDescription =
    t(currentPrize?.short_description) ||
    (isCurrentShopItemPrize
      ? getFirstFilledString(prizeData?.short_description, prizeData?.description)
      : null)

  const randomShift = useMemo(() => {
    const { shift } = settings
    const { field: currentPrizeField } = getReward(currentPrize, t)
    const availableShifts = prizes.slice(shift.min, shift.max + 1).reduce((acc, prize, index) => {
      const { field } = getReward(prize, t)
      if (currentPrizeField !== field) acc.push(index + shift.min)
      return acc
    }, [])
    return getRandomFromArray(availableShifts)
  }, [prizes, necessaryPrizeIndex, currentPrize])

  const backgroundData = useMemo(() => {
    const { prize, count } = elsOnBackgroundData
    if (typeof prize !== 'object' || typeof count !== 'number') return
    const containerWidth = backgroundContainerRef.current?.offsetWidth ?? globalThis.innerWidth ?? 0
    const {
      background: {
        elScale: [minScale, maxScale],
      },
    } = settings
    return new Array(count)
      .fill(prize)
      .reduce(
        (acc, { image }, index, { length }) => [
          ...acc,
          [
            minScale + ((maxScale - minScale) * index) / length,
            image,
            { x: getRandomFromRange(0, containerWidth), y: 0 },
          ],
        ],
        [],
      )
  }, [elsOnBackgroundData])

  const back = () => {
    setOnBackClick(true)

    callbacks.collectPendingGachaReward(pendingGachaReward)
    callbacks.getAttempts()

    const { type } = getReward({ ...currentPrize, hasBuff: prizeData?.hasBuff }, t)

    // При клике на забрать приз сначала меняем стейт и только затем
    // сбрасываем приз { id: null, type: null } по окончании анимации перехода между экранами
    const out = () => {
      setState('sets')
      setTimeout(() => callbacks.setActiveLootBoxPrize({ id: null, type: null }), 500)
    }

    const isWheelPromo = type === 'Promocode' || Boolean(prizeData?.promocode)

    ;({
      counter: out,
      static: () => {
        if (isCurrentShopItemPrize) return out()

        const { image: img, terms_of_use, promocode, link: linkBtn, description } = prizeData
        addModal({
          type: 'winModal',
          props: {
            className: !promocode ? 'win-modal_box-prize' : null,
            isHideScroll: !promocode,
            img,
            description,
            linkBtn,
            promocode,
            terms_of_use,
            callbacks: { out },
            func: out,
            isWheelPromo,
            expired_promo: prizeData.promocode_end_date,
            ...promocodeModal,
          },
        })
      },
    })[type]?.()
  }

  useEffect(() => {
    if (!id && !type && !onBackClick) setState('sets')
  }, [id, type, onBackClick])

  useEffect(() => {
    if (!id || !type || !pendingGachaReward?.openPayload) return
    callbacks.collectPendingGachaReward(pendingGachaReward)
    callbacks.getAttempts()
  }, [id, type, pendingGachaReward, callbacks])

  useEffect(() => {
    if (!isCurrentShopItemPrize || !isShowBackBtn) {
      setIsShopItemImageVisible(false)
      return
    }

    const timer = globalThis.setTimeout(() => {
      setIsShopItemImageVisible(true)
    }, SHOP_ITEM_IMAGE_SWAP_DELAY_MS)

    return () => globalThis.clearTimeout(timer)
  }, [isCurrentShopItemPrize, isShowBackBtn])

  useEffect(() => {
    if (!isCurrentShopItemPrize || !isShowBackBtn || !isShopItemImageVisible) {
      setIsShopItemUiVisible(false)
      return
    }

    const timer = globalThis.setTimeout(() => {
      setIsShopItemUiVisible(true)
    }, SHOP_ITEM_UI_SHOW_DELAY_MS)

    return () => globalThis.clearTimeout(timer)
  }, [isCurrentShopItemPrize, isShowBackBtn, isShopItemImageVisible])

  const isRewardInfoVisible = isCurrentShopItemPrize ? isShopItemUiVisible : isShowBackBtn

  wheelAnimationLogic({
    listRef,
    arrowRef,
    backgroundContainerRef,
    backgroundEls,
    counterParticlesRef,
    particlesContainerRef,
    prizeValueRef,
    prizesRef,
    currentAnimation,
    prizes,
    setPrizeValue,
    setElsOnBackgroundData,
    setIsShowBackBtn,
    isRollEnd,
    setIsRollEnd,
    backgroundData,
    necessaryPrizeIndex,
    t,
  })

  return (
    <div
      className={'wheel'}
      ref={ref}
    >
      <div className={'wheel__content'}>
        <div className={'wheel__header'}>
          <SwitchTransition>
            <CSSTransition
              key={Number(isRollEnd)}
              timeout={300}
              classNames={'wheel__title'}
            >
              <div className={'wheel__title'}>{safeHTML(t(isRollEnd ? titlePrize : title))}</div>
            </CSSTransition>
          </SwitchTransition>
        </div>

        <div className={'wheel__list-wrapper'}>
          <ul
            className={classNames('wheel__list')}
            ref={listRef}
          >
            {prizes.map((prize, id) => {
              const isNecessaryPrize = necessaryPrizeIndex === id
              const { type } = isNecessaryPrize && getReward(prize, t)
              const isCounter = type === 'counter'
              const prizeImage =
                isNecessaryPrize && isCurrentShopItemPrize
                  ? currentShopItemPreviewImage ?? prize.image
                  : prize.image
              const shouldSwapImage =
                isNecessaryPrize && isCurrentShopItemPrize && isShopItemImageVisible
              return (
                <li
                  key={`reward-${id}`}
                  ref={(ref) => (prizesRef.current[id] = ref)}
                  className={'wheel__reward'}
                  style={{ transform: `translateX(${(id - randomShift) * 120}%)` }}
                >
                  {isNecessaryPrize && !isCurrentShopItemPrize && (
                    <div
                      className={'wheel__background-pool'}
                      ref={backgroundContainerRef}
                    >
                      {backgroundData?.map?.(([size, image, { x, y }], i) => {
                        return (
                          <div
                            key={`background-el-${i}`}
                            ref={(ref) => (backgroundEls.current[i] = ref)}
                            className={'wheel__background-item'}
                            style={{
                              transform: `translate(${x}px, ${y}px) scale(${size})`,
                            }}
                          >
                            <img
                              src={image}
                              alt={'wheel-background-item'}
                            />
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <div className={'wheel__reward-icon'}>
                    <img
                      className={classNames(
                        'wheel__reward-icon-image',
                        'wheel__reward-icon-image_base',
                        {
                          'wheel__reward-icon-image_active': !shouldSwapImage,
                        },
                      )}
                      src={prize.image}
                      alt={''}
                    />
                    <img
                      className={classNames('wheel__reward-icon-image', {
                        'wheel__reward-icon-image_active': shouldSwapImage,
                      })}
                      src={prizeImage}
                      alt={''}
                    />
                  </div>

                  {isNecessaryPrize && (
                    <div
                      className={'wheel__reward-particles-container'}
                      ref={particlesContainerRef}
                    >
                      {settings.particles.arr.map((_, i) => (
                        <div
                          key={i}
                          ref={(ref) => (counterParticlesRef.current[i] = ref)}
                          className={'wheel__reward-particle'}
                        />
                      ))}
                    </div>
                  )}

                  <div
                    ref={(ref) => isNecessaryPrize && (prizeValueRef.current = ref)}
                    className={classNames('wheel__reward-value', {
                      'wheel__reward-value_active': isCounter,
                    })}
                  >
                    {isCounter && `+${prizeValue}`}
                  </div>
                </li>
              )
            })}
          </ul>

          <div
            style={{ transform: 'translateX(-50%)' }}
            className={classNames('wheel__arrow', { wheel__arrow_hidden: isRollEnd })}
            ref={arrowRef}
          >
            <Picture {...arrow} />
          </div>
        </div>

        <div className={'wheel__footer'}>
          {[rewardTitle, rewardDescription].some(Boolean) && (
            <div
              className={classNames('wheel__reward-info', {
                'wheel__reward-info_show': isRewardInfoVisible,
              })}
            >
              {!!rewardTitle && (
                <div className={'wheel__reward-info-title'}>{safeHTML(rewardTitle)}</div>
              )}

              {!!rewardDescription && (
                <div className={'wheel__reward-info-text'}>{safeHTML(rewardDescription)}</div>
              )}

              {prizeData?.promocode && prizeData?.promocode_end_date && (
                <div className={'wheel__promocode-date'}>
                  <div className={'wheel__promocode-text'}>
                    {safeHTML(
                      t('other.expiredAtWithValue', {
                        value: fromTimeStampFull(prizeData.promocode_end_date, isArabic),
                      }),
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <CustomButton
            {...button.attr}
            disabled={!isRewardInfoVisible}
            style={{ opacity: Number(isRewardInfoVisible) }}
            onClick={back}
            disposable={true}
          />
        </div>
      </div>
    </div>
  )
})
export default Wheel
Wheel.propTypes = {}
